/**
 * Local state for the anonymous (no-account) trial story.
 *
 * The backend returns a claim_token exactly once, when the story is created;
 * it lives only in this device's localStorage until the visitor registers or
 * logs in, at which point AuthContext sends it along and the story is
 * re-parented to the new account. Expired entries are dropped on read.
 *
 * A record imported into the app by its code (2026-09-26, TrialImportCard) has an EMPTY claim_token:
 * the look-up never hands the token out, so sign-ins send the code instead (AuthContext).
 */
export interface AnonStoryLocal {
  story_id: string;
  share_token: string;
  /** '' on a record imported by code. */
  claim_token: string;
  /** Typeable alias of the token (2026-09-17), absent on records from before it existed. */
  claim_code?: string | null;
  expires_at: string;
  child_name: string;
  child_age: number | null;
  language: string;
  /** The idea the story was created from (2026-09-13) — the duplicate guard compares the next tap against it. */
  theme?: string;
  /** Trial v3 (2026-09-21): the first name typed into the sheet, so the waiting screen and the countdown
   *  pill can say whose story is being made. Browser-only; the server never sees it as a field. */
  hero_name?: string | null;
}

import { anonStoriesApi } from './api';
import { getPlatform, isNativeApp } from './platform';
import { ageGateChildDeclared } from './ageGate';
import { getOrCreateInstallId } from './installId';
import { nativeTrialAvailable as nativeTrialAvailableCore } from './nativeTrialCore';
import { markCreatedThisLoad } from './trialReturn';
import type { AnonStatus, AnonStoryCreateData, AnonStoryResponse } from './types';
import type { AxiosResponse } from 'axios';

const KEY = 'snugtale_anon_story';
const CLAIMED_KEY = 'snugtale_claimed_story';

export function saveAnonStory(story: AnonStoryLocal): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(story));
  } catch { /* storage unavailable (private mode) — the story is still readable via its link */ }
}

export function getAnonStory(): AnonStoryLocal | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const story = JSON.parse(raw) as Partial<AnonStoryLocal>;
    if (!story.share_token || !story.story_id || !(story.claim_token || story.claim_code)) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (story.expires_at && Date.parse(story.expires_at) < Date.now()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return story as AnonStoryLocal;
  } catch {
    return null;
  }
}

/**
 * Same idea? Case- and whitespace-insensitive. The guard against minting a second story from
 * the untouched premise (2026-09-13: one parent tapped the prefilled idea three times, got three
 * near-identical Schlaflied stories, used up the per-IP cap and never saw that the box is editable).
 */
export function sameIdea(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
  return norm(a) === norm(b);
}

/** The code to claim with when this device holds a trial WITHOUT its token (imported into the app by code,
 *  2026-09-26) — every sign-in sends it instead; the server applies the per-account lockout. */
export function pendingClaimCode(): string | undefined {
  const r = getAnonStory();
  return r && !r.claim_token && r.claim_code ? r.claim_code : undefined;
}

export function clearAnonStory(): void {
  try {
    localStorage.removeItem(KEY);
  } catch { /* ignore */ }
}

/** The app's „Probegeschichte aus dem Browser übernehmen“ card (ClaimCodeCard) is done on this device: dismissed, or a
 *  trial was claimed here by any path — an account redeems one trial, so a later code could only answer 409
 *  (review 2026-10-20: the card kept asking to paste after the trial was already in the account). */
const CLAIM_CODE_CARD_KEY = 'snugtale_claim_code_card_v1';
export function claimCodeCardDone(): boolean {
  try { return localStorage.getItem(CLAIM_CODE_CARD_KEY) === '1'; } catch { return false; }
}
export function markClaimCodeCardDone(): void {
  try { localStorage.setItem(CLAIM_CODE_CARD_KEY, '1'); } catch { /* ignore */ }
}

/** Hand the just-claimed story id to HomePage across the post-auth redirect. Every claim path calls it. */
export function setClaimedStoryId(storyId: string): void {
  markClaimCodeCardDone();
  try {
    sessionStorage.setItem(CLAIMED_KEY, storyId);
  } catch { /* ignore */ }
}

export function consumeClaimedStoryId(): string | null {
  try {
    const id = sessionStorage.getItem(CLAIMED_KEY);
    if (id) sessionStorage.removeItem(CLAIMED_KEY);
    return id;
  } catch {
    return null;
  }
}

/** True while a just-claimed trial story is waiting for HomePage to open it. */
export function hasPendingClaimedStory(): boolean {
  try {
    return !!sessionStorage.getItem(CLAIMED_KEY);
  } catch {
    return false;
  }
}

/**
 * POST /api/anon/stories with the native extras: the apps send their platform
 * and the per-install identifier (lib/installId.ts) so the server can cap per
 * install instead of per IP. Browsers send the payload untouched.
 */
export async function createAnonStory(data: AnonStoryCreateData): Promise<AxiosResponse<AnonStoryResponse>> {
  const res = isNativeApp()
    ? await anonStoriesApi.create({ ...data, platform: getPlatform(), install_id: await getOrCreateInstallId() })
    : await anonStoriesApi.create(data);
  // The hand-off to the new story in this page load is no "return" (lib/trialReturn.ts).
  markCreatedThisLoad(res.data?.share_token);
  return res;
}

const OVERRIDE_KEY = 'snugtale_native_trial_override';

/** Tester override (see nativeTrialCore): set from the Settings tester card, or
 *  `localStorage.setItem('snugtale_native_trial_override','1')` in a browser. */
export function testerTrialOverride(): boolean {
  try { return localStorage.getItem(OVERRIDE_KEY) === '1'; } catch { return false; }
}

export function setTesterTrialOverride(on: boolean): void {
  try { if (on) localStorage.setItem(OVERRIDE_KEY, '1'); else localStorage.removeItem(OVERRIDE_KEY); } catch { /* ignore */ }
}

/** Live wrapper of the pure rule in lib/nativeTrialCore.ts. */
export function nativeTrialAvailable(status: AnonStatus | null | undefined): boolean {
  return nativeTrialAvailableCore(status, {
    isNative: isNativeApp(), childDeclared: ageGateChildDeclared(), override: testerTrialOverride(),
  });
}

const STATUS_FALLBACK: AnonStatus = { enabled: false, page_count: 5, native_enabled: false };
let statusPromise: Promise<AnonStatus> | null = null;

/** One GET /api/anon/status per page load, shared by every native entry point. */
export function loadAnonStatus(): Promise<AnonStatus> {
  if (!statusPromise) {
    statusPromise = anonStoriesApi.status()
      .then((r) => ({ ...STATUS_FALLBACK, ...r.data }))
      .catch(() => STATUS_FALLBACK);
  }
  return statusPromise;
}
